/**
 * Through `useDayJs`, which extends the `utc` and `timezone` plugins every `.tz()`
 * call here depends on. A bare `dayjs` import leaves that to whatever else in the
 * bundle happens to have extended it — see the same fix in `SalesSwimlane.vue`,
 * where removing one unrelated import made every `.tz()` throw.
 */
import useDayJs from '@plugins/useDayJs';

const dayjs = useDayJs();

/**
 * Rendering an order action's scheduled moment.
 *
 * `schedule_at` holds one of two different things, and which one is recorded by
 * the row's `all_day` flag:
 *
 * - `all_day: false` — a true UTC instant. Convert it to the viewer's timezone.
 * - `all_day: true`  — a naive **local** date pinned to midnight whose time
 *   component carries no meaning. It must never be passed through a timezone
 *   conversion: doing so moves it onto the previous day for any viewer behind
 *   UTC, which is the misleading-midnight defect these helpers exist to avoid.
 */
export interface SalesActionSchedule {
    schedule_at?: string | null;
    all_day?: boolean | null;
}

const DATE_FORMAT = 'DD/MM/YY';
const TIME_FORMAT = 'h:mm A';

/**
 * The date part of a naive local value, read as text rather than parsed as an
 * instant. Ten characters is the `YYYY-MM-DD` prefix every serialised form of
 * the column shares.
 */
const localDatePart = (value: string): string => value.slice(0, 10);

export function formatSalesActionDate(action: SalesActionSchedule, timezone: string, placeholder = '--'): string {
    if (!action.schedule_at) {
        return placeholder;
    }

    if (action.all_day) {
        return dayjs(localDatePart(String(action.schedule_at))).format(DATE_FORMAT);
    }

    return dayjs(action.schedule_at).tz(timezone).format(DATE_FORMAT);
}

/**
 * The time of day, for a timed action only. An all-day action has no meaningful
 * time, so this returns an empty string rather than a fabricated midnight —
 * callers label those rows "All day" instead.
 */
export function formatSalesActionTime(action: SalesActionSchedule, timezone: string): string {
    if (!action.schedule_at || action.all_day) {
        return '';
    }

    return dayjs(action.schedule_at).tz(timezone).format(TIME_FORMAT);
}

/**
 * The date the action belongs to, as `YYYY-MM-DD`, for anything that files rows
 * under a day.
 *
 * The same two-shape branch as the formatters above, and here for that reason: a
 * grouping that took `schedule_at.slice(0, 10)` from every row would file a timed
 * action under its *UTC* date, so a 9am Brisbane call would appear on the previous
 * day for the ten hours either side of midnight — and one that ran every row
 * through `.tz()` would do the mirror-image damage to the all-day rows. Neither
 * failure is visible in a viewer sitting on UTC, which is why it is stated once.
 */
export function salesActionDayKey(action: SalesActionSchedule, timezone: string): string | null {
    if (!action.schedule_at) {
        return null;
    }

    if (action.all_day) {
        return localDatePart(String(action.schedule_at));
    }

    return dayjs(action.schedule_at).tz(timezone).format('YYYY-MM-DD');
}
